import { JSX, KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';
import { ReactSVG } from 'react-svg';
import StyledPromptForm from '../../styles/StyledPromptForm';
import StyledTextArea from '../../styles/StyledTextArea';
import sendMessageIconUrl from '../../assets/svgs/sendMessageIcon.svg';
import StyledSendButton from '../../styles/StyledSendButton';

import { useForm, SubmitHandler } from 'react-hook-form';
import AppContext from '../../contexts/AppContext';
import AppContextType from '../../types/AppContextType';

import ChatsContext from '../../contexts/ChatsContext';
import ChatsContextType from '../../types/ChatsContextType';
import Rating from '../Rating/Rating';

interface PromptFormProps {
  disabled: boolean;
  showRating: boolean;
}

const PromptForm = ({ disabled, showRating }: PromptFormProps): JSX.Element => {
  const { configuration } = useContext<AppContextType>(AppContext);
  const { chats, sendChatMessage } = useContext<ChatsContextType>(ChatsContext);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [userIsTyping, setUserIsTyping] = useState(false);

  if (!configuration) return <></>;

  type Inputs = {
    text: string
  }

  const {
    register,
    handleSubmit,
    reset
  } = useForm<Inputs>();

  const { ref, onChange, ...rest } = register('text');

  const submitAction = (data: {text: string}) => {
    sendChatMessage(data.text);
    reset();
  };

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    if (shouldDisableSendButton()) return;
    if (!data) return;
    if (data.text.length === 0) return;

    submitAction(data);
    adjustTextAreaHeight();
    setUserIsTyping(false);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  const onKeyUp = () => {
    if (!textAreaRef.current) return;

    setUserIsTyping(textAreaRef.current.value.length > 0);
  };

  const adjustTextAreaHeight = () => {
    if (!textAreaRef.current) return;

    const textarea = textAreaRef.current;
    if (!textarea) return;

    // Temporarily disable scroll to prevent flickering
    textarea.style.overflow = 'hidden';
    // Reset height to 'auto' to get the correct scrollHeight
    textarea.style.height = 'auto';
    // Adjust height based on scrollHeight
    textarea.style.height = `${textarea.scrollHeight}px`;

    if (textarea.scrollHeight > 36) {
      textarea.style.marginBottom = '10px';
    } else {
      textarea.style.marginBottom = '0px';
    }
    // Re-enable scroll if needed
    textarea.style.overflow = '';
  };

  const getPlaceholderText = () => {
    const defaultPlaceholder = 'Type your message';
    if (!configuration) return defaultPlaceholder;
    if (!configuration.translated_labels) return defaultPlaceholder;

    return configuration.translated_labels.user_chat_placeholder;
  };

  const hasChatFromUser = () => {
    return chats.some((chat) => chat.role === 'user');
  };

  const shouldShowRating = () => {
    return showRating && !userIsTyping && hasChatFromUser();
  };

  const shouldDisableSendButton = () => {
    return disabled || !userIsTyping;
  };

  useEffect(() => {
    textAreaRef.current?.focus();
  }, []);

  return (
    <StyledPromptForm onSubmit={handleSubmit(onSubmit)}>
      <StyledTextArea
        ref={(element) => {
          ref(element);
          textAreaRef.current = element;
        }}
        onChange={(element) => {
          onChange(element);
          adjustTextAreaHeight();
        }}
        maxLength={configuration.max_message_characters}
        required
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        placeholder={getPlaceholderText()}
        {...rest}
      />

      {
        shouldShowRating() ?
          <Rating />
        :
          <StyledSendButton type='submit'>
            <ReactSVG
              src={sendMessageIconUrl}
              wrapper='span'
              afterInjection={(svg) => {
                svg.classList.add('svg-send-button');
                document.querySelector('.svg-send-button path')?.setAttribute('style', `fill: ${shouldDisableSendButton() ? '#bbb' : configuration.theme_color}`);
              }}
            />
          </StyledSendButton>
      }
    </StyledPromptForm>
  );
};

export default PromptForm;